import { makeVar, useQuery, useReactiveVar } from '@apollo/client'
import { useGridApiRef } from '@mui/x-data-grid-pro'
import { ConnectionIcon } from '@sitoo/mui-components'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { SettingsList } from '../../../components/settings-list'
import { ApiKeyType, GetApiKeysDocument } from '../../../generated/graphql'
import { ApiKeyFilterPanel } from './api-key-filter-panel'
import { PageHeader } from '../../../components/page-header'
import { FilterProvider } from '../../../components/data-grid/context'
import { useFilterContext } from '../../../components/data-grid/hooks/use-filter-context'
import { ApiKeyFilter } from './api-key-filter'
import { useSortItems } from './hooks/sort-items'
import { Row, useApiKeyColumns } from './hooks/data-columns'
import { TextFilter } from '../../../components/data-grid/filters/text-filter'
import { CursorPagination } from '../../../components/data-grid/cursor-pagination'
import { enqueueSnackbar } from 'notistack'
import { ApiKeyViewPanel } from './api-key-view-panel'

const typeValues: Record<string, ApiKeyType> = {
  normal: ApiKeyType.Normal,
  giftCard: ApiKeyType.GiftCard,
  pos: ApiKeyType.Pos,
}

export type ApiKeyViewPanelState = {
  id: string | null
  siteId?: number
  isOpen: boolean
}

export const reactiveApiKeyFilterViewPanelVar = makeVar({
  isOpen: false,
})

export const reactiveApiKeyViewPanelVar = makeVar({
  isOpen: false,
  id: 0,
})

type Filter = ApiKeyFilter & TextFilter

const PAGE_SIZE = 100

export const ApiKeysList = () => {
  const apiRef = useGridApiRef()
  const { t } = useTranslation(['api_keys'])
  const filterContext = useFilterContext<Filter>()
  const { sortItems, getSort } = useSortItems()
  const sortModel = apiRef.current?.state?.sorting.sortModel

  const queryVariables = useMemo(() => {
    const typeValue = filterContext.filter.type?.value || 'normal'
    return {
      start: 0,
      num: PAGE_SIZE,
      searchtext: filterContext.filter.text?.value,
      type: typeValues[typeValue] || undefined,
      enabled: filterContext.filter.status
        ? filterContext.filter.status.value === 'active'
        : undefined,
      sort: sortModel ? getSort(sortModel)?.value : undefined,
    }
  }, [filterContext.filter, getSort, sortModel])

  const {
    data,
    loading: fetchLoading,
    fetchMore,
  } = useQuery(GetApiKeysDocument, {
    skip: !filterContext.isFilterReady,
    variables: queryVariables,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    onError(error) {
      enqueueSnackbar({
        message: error.message,
        variant: 'error',
      })
    },
  })
  const dataGridColumns = useApiKeyColumns()
  const filterViewPanelState = useReactiveVar(reactiveApiKeyFilterViewPanelVar)
  const viewPanelState = useReactiveVar(reactiveApiKeyViewPanelVar)

  const rows = useMemo(() => data?.apiKeys.items || [], [data?.apiKeys.items])

  const openFilterViewPanel = () => {
    reactiveApiKeyViewPanelVar({
      isOpen: false,
      id: 0,
    })
    reactiveApiKeyFilterViewPanelVar({
      isOpen: true,
    })
  }

  const fetchMoreItems = useCallback(() => {
    if (data?.apiKeys.totalcount) {
      return fetchMore({
        variables: {
          start: data.apiKeys?.items?.length || 0,
          num: PAGE_SIZE,
        },
      })
    }
  }, [data, fetchMore])

  return (
    <FilterProvider value={filterContext}>
      <SettingsList
        apiRef={apiRef}
        name="api-keys-list"
        columns={dataGridColumns}
        rows={rows}
        rowCount={rows.length}
        getRowId={(row: Row) => row.id}
        loading={fetchLoading}
        noRowsOverlay={{
          icon: <ConnectionIcon />,
          title: t('api_keys:no_api_keys_title'),
          description: t('api_keys:no_api_keys_description'),
        }}
        rowHeight={50}
        columnVisibilityModel={{
          name: true,
          type: true,
          ip_restrictions: true,
        }}
        hasPageHeader
        onShowFilter={openFilterViewPanel}
        slots={{
          FilterPanel: ApiKeyFilterPanel,
          PageHeader,
          pagination: CursorPagination,
          ViewPanel: ApiKeyViewPanel,
        }}
        hasTextFilter
        sortItems={sortItems}
        slotProps={{
          filterPanel: {
            isOpen: filterViewPanelState.isOpen,
            onClose: () => {
              reactiveApiKeyFilterViewPanelVar({
                isOpen: false,
              })
            },
          },
          pageHeader: {
            title: t('api_keys:page_title'),
            isFlexible: true,
            rightColumn: null,
          },
          viewPanel: {
            isOpen: viewPanelState.isOpen,
            onClose: () => {
              reactiveApiKeyViewPanelVar({
                isOpen: false,
                id: 0,
              })
            },
            apiKeyId: viewPanelState.id,
          },
        }}
        paginationModel={{ page: 0, pageSize: PAGE_SIZE }}
        fetchMore={fetchMoreItems}
        showMore={
          Number(data?.apiKeys.items?.length) < Number(data?.apiKeys.totalcount)
        }
        onRowClick={(row) => {
          reactiveApiKeyFilterViewPanelVar({
            isOpen: false,
          })
          reactiveApiKeyViewPanelVar({
            isOpen: true,
            id: Number(row.id),
          })
        }}
      />
    </FilterProvider>
  )
}
